import { first } from 'lodash';
import { keepPreviousData, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { DocumentLibraryService } from '@dealstack/frontend/shared/dataExchange/services/document-library/document-library.service';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import {
	TableOperations,
	TableSorting,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { GetDocumentsRes } from '@dealstack/shared/lib/generated';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';

type DocumentsWithFilteringQueryKey = [
	string,
	{
		pagination: TableOperations<RecordAny>['pagination'];
		sortBy: TableSorting | undefined;
		classificationId?: string;
		classificationGroup?: string;
		searchQuery?: string;
		fieldFilter?: RecordAny;
		excludeWorkflowGenerated?: boolean;
	},
];

type BuildDocumentsWithFilteringParams = {
	dealId: string;
	dataOperations: TableOperations<RecordAny> | null;
	classificationId?: string;
	classificationGroup?: string;
	excludeWorkflowGenerated?: boolean;
};

interface Options
	extends Omit<
		UseQueryOptions<ApiV1.Response<GetDocumentsRes>, unknown, GetDocumentsRes, [string]>,
		'queryKey' | 'queryFn' | 'keepPreviousData' | 'select'
	> {
	dealId: string;
	dataOperations: TableOperations<RecordAny> | null;
	classificationId?: string;
	classificationGroup?: string;
	excludeWorkflowGenerated?: boolean;
}

export const buildDocumentsWithFilteringQueryKey = (
	formattedUnicorn: string,
	params: BuildDocumentsWithFilteringParams
): DocumentsWithFilteringQueryKey => [
	formattedUnicorn,
	{
		pagination: params.dataOperations?.pagination,
		sortBy: first(params.dataOperations?.sortBy),
		classificationId: params.classificationId,
		classificationGroup: params.classificationGroup,
		searchQuery: params.dataOperations?.search,
		fieldFilter: params.dataOperations?.filter,
		excludeWorkflowGenerated: params.excludeWorkflowGenerated,
	},
];

export const useDocumentsWithFiltering = (options: Options) => {
	const {
		dealId,
		dataOperations,
		classificationId,
		classificationGroup,
		excludeWorkflowGenerated,
	} = options;
	const formattedUnicorn = formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, {
		dealId,
	});
	const queryKey = buildDocumentsWithFilteringQueryKey(formattedUnicorn, {
		dealId,
		dataOperations,
		classificationId,
		classificationGroup,
		excludeWorkflowGenerated,
	});
	return useQuery({
		queryKey,
		queryFn: ({ signal }) =>
			DocumentLibraryService.getWithFilteringAndOrder(queryKey[0], queryKey[1], signal),
		select: (data) => data.payload,
		placeholderData: keepPreviousData,
	});
};
